import { useAppDispatch, useAppSelector } from '@/shared/lib/redux/hooks';
import { currentStoreChanged } from '@/shared/lib/redux/actions';
import { nextCurrentStoreId } from '../lib/navigation';
import { useLazyListMembershipsQuery } from '../api/authApi';

/** Re-resolves `store.currentStoreId` against the signed-in user's live memberships: keeps the
 *  persisted selection while it is still an APPROVED membership, otherwise picks the first
 *  APPROVED one (never a pending/rejected/blocked one) — see `pickCurrentStoreId`. Needed anywhere
 *  a session starts mid-SPA-lifetime without a full reload, e.g. `LoginForm`, where a stale/null
 *  `currentStoreId` (cleared by a prior logout) would otherwise send `RootRedirect`/
 *  `RequireMembership` to the wrong zone, and after leaving a store. Also run on page load by
 *  `useBootstrap`. Resolves to `false` when the memberships could not be fetched: the current
 *  selection is then left untouched (a failed fetch is not «no memberships»). */
export function useResolveCurrentStore() {
  const dispatch = useAppDispatch();
  const currentStoreId = useAppSelector((state) => state.store.currentStoreId);
  const [fetchMemberships] = useLazyListMembershipsQuery();

  return async (): Promise<boolean> => {
    const memberships = await fetchMemberships()
      .unwrap()
      .catch(() => null);
    const next = nextCurrentStoreId(memberships, currentStoreId);
    if (next !== currentStoreId) {
      dispatch(currentStoreChanged(next));
    }
    return memberships !== null;
  };
}
